// VMS 的設備模型：把各家戶的綁定列（GET /v1/homes/{id}.things）整理成「設備 → 通道 / 子設備」樹。
//
// 定址鍵是 device_ref（SPEC #terms）：原生設備 = device_id；子設備 = `<parent>:ch<index>`（component/protocol ParseChildAID，ADR-004）：
// NVR 的通道、station 的 HaLow 攝影機 / ONVIF 通道。子設備沒有自己的連線，在線狀態跟 parent。
// VmsDevice.aid / parentAid 是這個 device_ref（欄名沿用）。
// 攝影機判定（W1 過渡）：子設備、或 device_id 型態碼 CAM；W2 起改看 canonical capability（kinio_streaming）。
// 子設備不一定是攝影機：Sub-GHz 閘道底下的感測器（DEV-04，protocol "subg"）type_code 是 S 開頭（例 SLK 漏水）——
// type_code 不是 C（Cameras）開頭或 protocol 是 subg 就不當攝影機。

import type { HomeThingBinding, KinioHome } from '@kinio/web-sdk';
import type { ChannelAbilities } from './caps';
import type { MeDenied, MeOtherSource, MeShare, Source } from './myDevices';
import type { TtlTarget } from '../features/devices/ttlTarget';

/**
 * 這台現在的有效權限（GET /v1/me/devices，SPEC #device-permissions-ui）。按鈕只看這裡，不自己推權限；直接打 API 仍逐次驗權。
 * 通道（子設備）的 actions 來自 parent 的 children[]；can_manage 跟 parent。
 */
export interface DeviceAccess {
  source: Source;
  actions: string[];
  can_manage: boolean;
  denied: MeDenied[];
  expires_at: string | null;
  share: MeShare | null;
  other_sources: MeOtherSource[];
  /** 看不到本體、只看得到通道的 parent */
  container_only: boolean;
}

/** 這台能不能做 action（沒有 access 一律不行）。 */
export function can(d: { access?: DeviceAccess } | null | undefined, action: string): boolean {
  return !!d?.access && d.access.actions.includes(action);
}

/** 管理動作（解綁、OTA、改名、改權限、分享…）：只看 can_manage。 */
export function canManage(d: { access?: DeviceAccess } | null | undefined): boolean {
  return !!d?.access?.can_manage;
}

export const CHILD_SEP = ':ch';

export type Presence = 'online' | 'offline' | 'unknown';

export interface VmsDevice {
  /** `<home_id>/<aid>`，UI 的穩定 key */
  key: string;
  homeId: string;
  homeName: string;
  apiEndpoint: string;
  aid: string;
  name: string;
  model: string;
  /** Kinio Node / Wi-Fi 基地台的 MAC 後 6 碼（things[].mac_suffix；機身標籤印的）：搜尋也比對它（SPEC #notify-177，Eddy 2026-10-16 選單 A）。 */
  macSuffix?: string;
  /** 平台的機種顯示名（model_name；SPEC #vms-web「機種顯示名 model_name / model_source」）；沒有 = undefined */
  modelName?: string;
  /** catalog / reported / generic（RTSP 直連，沒有型號資訊）；沒有 = undefined */
  modelSource?: string;
  kind: string;
  firmware: string;
  presence: Presence;
  lastSeen: string;
  parentAid: string | null;
  channel: number | null;
  isCamera: boolean;
  children: VmsDevice[];
  /** thing 的 AID 型態碼（子設備 由 parent 報，NVR 通道 CAM、Sub-GHz 子設備例 SLK）。 */
  typeCode?: string;
  /** 子設備 的接法（onvif / halow / subg …，gateway children 幀的 protocol）。 */
  protocol?: string;
  /**
   * VMS façade（/vms/devices）回的生效能力（thing.capabilities.effective 的 cluster 名；SPEC #contract-app-ui）；
   * 沒宣告 = []、façade 還沒回來 = undefined。按鈕 / 設定出不出現只看它（通道看 channelAbilities，lib/caps.ts）。
   */
  capabilities?: string[];
  /** façade capabilities_declared：設備宣告過能力（false = 舊設備，控制鈕退回原本的判斷，lib/caps.ts）；façade 還沒回來 = undefined。 */
  capsDeclared?: boolean;
  /** façade reported_clusters：shadow 報過哪些 cluster（判資料來源，例 kinio_storage → 健康分頁；能力只在舊設備退回時看它，lib/caps.ts）。 */
  reportedClusters?: string[];
  /**
   * façade storage_kinds：shadow kinio_storage.disks 錄影碟的 kind 去重（sd / hdd / ssd / usb；"" = 舊形狀沒帶 kind，當本機磁碟）。
   * 本地錄影主機只看它（SPEC #vms-web-s5-19-1 STN-01）；子設備 / façade 還沒回來 = undefined。
   */
  storageKinds?: string[];
  /**
   * 工位模式（shadow kinio_station.info.seat_mode = true；SPEC #vms-web-s5-19-1 STN-01 工位模式）：不是本地錄影主機、
   * 不列 ONVIF 閘道、不做錄影健康判斷。只有頂層 Station 會帶；沒報 / 還沒讀到 = undefined（照一般規則）。
   */
  seatMode?: boolean;
  /** 通道（子設備）的 kinio_channel.abilities（façade channel_abilities）；沒報 = null，façade 還沒回來 = undefined。 */
  channelAbilities?: ChannelAbilities | null;
  mediaStatus?: string;
  /** product_models 的 device_class（façade；查不到 / 還沒回來為 undefined） */
  deviceClass?: string;
  /**
   * façade 的 live_limit：這台所在「有連線的設備」同時觀看的上限（通道回 parent 的）——多路設備 = 路數 × 2（主 + 子碼流）、
   * 單路攝影機 4（vms-system §5.1，Eddy 2026-09-28）。façade 還沒回來 / 問不到為 undefined。
   */
  liveLimit?: number;
  /** façade presence.reason（離線原因：keepalive_timeout / gateway_lost / missed_check_in …）；沒有就 undefined。DEV-01 / DEV-10 */
  presenceReason?: string;
  /** façade 的 cert_not_after：這台 AID 憑證到期（RFC3339；DEV-10 / HEALTH-01）。子設備 沒有。 */
  certNotAfter?: string;
  /** 使用者沒取名、名字是這裡暫時組的（機種＋末 4 碼）：「我的設備」有平台的 display_name 時換成平台的（第七輪 B7-07） */
  autoName?: boolean;
  /**
   * Kinio Node 的 UART1 接著哪台（façade ttl_target，SPEC NODE-06 / #node-s11-7-3）：null = façade 回來了、Node 沒回報；
   * façade 還沒回來 / 沒有這台 = undefined（概覽不先寫「還沒設定」）。
   */
  ttlTarget?: TtlTarget | null;
  /** 「我的設備」的有效權限（lib/useDevices.ts withAccess）；不在「我的設備」裡 = undefined（不列）。 */
  access?: DeviceAccess;
}

export function parseChildAid(aid: string): { parent: string; index: number } | null {
  const i = aid.lastIndexOf(CHILD_SEP);
  if (i <= 0) return null;
  const n = Number(aid.slice(i + CHILD_SEP.length));
  if (!Number.isInteger(n) || n < 0 || aid.slice(i + CHILD_SEP.length) === '') return null;
  return { parent: aid.slice(0, i), index: n };
}

function presenceOf(b: HomeThingBinding): Presence {
  const s = b.thing?.presence?.state;
  return s === 'online' ? 'online' : s === 'offline' ? 'offline' : 'unknown';
}

/**
 * 頂層設備算攝影機的 TYPE（SPEC：攝影機類 = CAM / CVD / CFL，同 App KinioDeviceKind）。CSN（只拍快照）不給即時，不算。
 * 先看 façade 的 type_code，沒有才看 AID 前 3 碼。投光燈攝影機（CFL，例 PAC142-T41）原本只認 CAM- 而沒有即時 / 錄影分頁。
 */
const CAMERA_TYPES = new Set(['CAM', 'CFL', 'CVD']);

/** MyCam 手機攝影機（SPEC #mycam-v1）：軟體設備 TYPE SFT、機種 mycam-phone（電腦版 mycam-node 不算；同 App KinioDeviceKind） */
export const MYCAM_PHONE_MODEL = 'mycam-phone';
export function isTopLevelCamera(typeCode: string | undefined, ref: string, model: string | undefined): boolean {
  const code = (typeCode ?? ref.slice(0, 3)).toUpperCase();
  return CAMERA_TYPES.has(code) || (code === 'SFT' && model === MYCAM_PHONE_MODEL);
}

/** 一個家戶的綁定列 → 頂層設備（子設備掛在 parent.children，依通道排序）。 */
export function buildDeviceTree(home: KinioHome, things: HomeThingBinding[]): VmsDevice[] {
  const homeName = home.display_name || home.home_id;
  const byAid = new Map<string, VmsDevice>();
  const mk = (b: HomeThingBinding): VmsDevice => {
    const ref = b.device_ref;
    const child = parseChildAid(ref);
    const thing = b.thing?.thing;
    const channelLabel = child ? `CH${child.index + 1}` : '';
    const typeCode = typeof thing?.type_code === 'string' && thing.type_code ? thing.type_code : undefined;
    const protocol = typeof thing?.attrs?.protocol === 'string' ? (thing.attrs.protocol as string) : undefined;
    const childIsCamera = !!child && protocol !== 'subg' && (!typeCode || typeCode.startsWith('C'));
    return {
      key: `${home.home_id}/${ref}`,
      homeId: home.home_id,
      homeName,
      apiEndpoint: home.endpoints.api,
      aid: ref,
      // 沒取名時不用機種代號（kinio-node-bl602）當名字：機種友善名＋序號末 4 碼（第三輪 R3-P1-5、B3-08；樹、清單、地圖同一個名字）
      name: b.display_name || channelLabel || (thing?.model ? `${modelLabel(thing.model)} ${aidTail(ref)}` : ref),
      ...(!b.display_name && !child ? { autoName: true } : {}),
      model: thing?.model ?? '',
      ...modelNameOf(thing as Record<string, unknown> | undefined),
      ...macSuffixOf(b as unknown as Record<string, unknown>),
      kind: child ? 'child' : b.kind,
      firmware: thing?.fw_version ?? '',
      presence: presenceOf(b),
      lastSeen: b.thing?.presence?.last_seen_at ?? b.thing?.presence?.connected_at ?? '',
      parentAid: child?.parent ?? null,
      channel: child?.index ?? null,
      isCamera: childIsCamera || (!child && isTopLevelCamera(typeCode, ref, thing?.model)),
      typeCode,
      protocol,
      children: [],
    };
  };
  const all = things.map(mk);
  for (const d of all) byAid.set(d.aid, d);
  const top: VmsDevice[] = [];
  for (const d of all) {
    const parent = d.parentAid ? byAid.get(d.parentAid) : undefined;
    if (parent) {
      if (d.presence === 'unknown') d.presence = parent.presence;
      parent.children.push(d);
    } else {
      top.push(d);
    }
  }
  for (const d of top) d.children.sort((a, b) => (a.channel ?? 0) - (b.channel ?? 0));
  // 有通道的（NVR / station）先、在線的先，再依名稱
  const rank = (d: VmsDevice) => (d.children.length ? 0 : 1) * 2 + (d.presence === 'online' ? 0 : 1);
  return top.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** 攤平成可觀看的攝影機（子通道 + 獨立攝影機），在線的先。 */
export function camerasOf(tree: VmsDevice[]): VmsDevice[] {
  const out: VmsDevice[] = [];
  for (const d of tree) {
    if (d.isCamera) out.push(d);
    for (const c of d.children) if (c.isCamera) out.push(c);
  }
  return out.sort((a, b) => (a.presence === 'online' ? 0 : 1) - (b.presence === 'online' ? 0 : 1));
}

/** 顯示用的全名：通道帶上 parent 名稱（「NVR-大廳 / CH2」）。 */
export function fullName(d: VmsDevice, tree: VmsDevice[]): string {
  if (!d.parentAid) return d.name;
  const p = tree.find((x) => x.aid === d.parentAid && x.homeId === d.homeId);
  return p ? `${p.name} / ${d.name}` : d.name;
}

/**
 * 事件 / 警報列的設備標籤（第七輪 B7-02：總覽警報列與警報分頁同一個函式）：
 * 清單裡有 → 完整名；NVR / Station 沒登記的通道 →「NVR631 / CH5（未登記的通道）」；都找不到 →「不在清單裡的設備」。
 * 不退回只寫 parent 名稱（三列「影像訊號中斷 · NVR631」分不出來）。
 */
export function eventDeviceLabel(
  t: (k: string, o?: Record<string, unknown>) => string,
  find: (ref: string) => VmsDevice | undefined,
  tree: VmsDevice[],
  ref: string,
): string {
  const d = find(ref);
  if (d) return fullName(d, tree);
  const ch = parseChildAid(ref);
  const p = ch ? find(ch.parent) : undefined;
  return p && ch ? t('events.unregistered_channel', { parent: fullName(p, tree), n: ch.index + 1 }) : t('events.device_unknown');
}

/**
 * 只有 device_ref + 名稱的清單（規則目錄、分享的設備…）裡的完整名（B-23：顯示名稱一律完整名，「NVR631 / CH5」不是一排 CH1、CH2）：
 * 通道 = <parent 名稱> / <通道名>；parent 不在清單就照原名。
 */
export function refFullName(ref: string, name: string | undefined, list: { device_ref?: string; ref?: string; display_name?: string; name?: string }[]): string {
  const own = name || ref;
  const ch = parseChildAid(ref);
  if (!ch) return own;
  const p = list.find((x) => (x.device_ref ?? x.ref) === ch.parent);
  const pn = p ? p.display_name || p.name : '';
  return pn ? `${pn} / ${own}` : own;
}

/** 家戶名稱：同名的家戶（例：兩個組織都有 lab）後面帶組織名，下拉選單才分得出來（B-23）。 */
export function homeLabels<H extends { home_id: string; display_name?: string; org_id?: string | null }>(homes: H[], orgName: (orgId: string) => string | undefined): Map<string, string> {
  const name = (h: H) => h.display_name || h.home_id;
  const count = new Map<string, number>();
  for (const h of homes) count.set(name(h), (count.get(name(h)) ?? 0) + 1);
  return new Map(
    homes.map((h) => {
      const org = h.org_id ? orgName(h.org_id) : undefined;
      return [h.home_id, (count.get(name(h)) ?? 0) > 1 && org ? `${org} › ${name(h)}` : name(h)];
    }),
  );
}

/** Kinio Node / Wi-Fi 基地台：機身標籤印 MAC 後 6 碼，輸入去冒號 / 連字號、不分大小寫是 mac_suffix 的子字串就算（SPEC #notify-177）。 */
export function macMatches(macSuffix: string | null | undefined, q: string): boolean {
  const hex = q.trim().toLowerCase().replace(/[:-]/g, '');
  return !!hex && !!macSuffix && macSuffix.toLowerCase().includes(hex);
}

/** 設備列 / 設備頁標題下的副標「MAC …3E82E3」（沒有 mac_suffix 不畫）。 */
export function macLabel(macSuffix: string | null | undefined): string | null {
  return macSuffix ? `MAC …${macSuffix.toUpperCase()}` : null;
}

export function deviceMatches(d: VmsDevice, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return macMatches(d.macSuffix, n) || [d.name, d.model, d.aid, d.homeName].some((v) => v.toLowerCase().includes(n)) || d.children.some((c) => deviceMatches(c, q));
}

export type SensorKind = 'contact' | 'motion';

/** 感測器種類（AID TYPE：SCT 門窗、SOC 佔用 / PIR；或型號帶 contact / pir）。攝影機、有通道的設備不算。 */
export function sensorKind(d: Pick<VmsDevice, 'aid' | 'model' | 'isCamera'> & { typeCode?: string }): SensorKind | null {
  if (d.isCamera) return null;
  const aid = d.typeCode ? d.typeCode + '-' : d.aid.split(CHILD_SEP)[0];
  const m = d.model.toLowerCase();
  if (aid.startsWith('SCT-') || /contact|door|reed/.test(m)) return 'contact';
  if (aid.startsWith('SOC-') || /pir|occupancy|motion/.test(m)) return 'motion';
  return null;
}

/** 感測器事件 → 觸發中（門開 / 有人）或復歸；不是感測器狀態事件回 null。 */
export function sensorActive(type: string, payload: Record<string, unknown> | undefined): boolean | null {
  if (type === 'boolean_state.state_change' && typeof payload?.state_value === 'boolean') return !payload.state_value; // true = 閉合
  if (type.startsWith('occupancy_sensing.') && typeof payload?.occupancy === 'boolean') return payload.occupancy;
  if (type.startsWith('occupancy_sensing.') && typeof payload?.occupancy === 'number') return payload.occupancy !== 0;
  return null;
}

/**
 * 全站同一套設備數（SPEC #vms-web-827「總覽的數字與版面」，VMS UX 評審 2026-10-13 P0-8、B-08）：
 *   設備 = 實體台數（一台 NVR 算 1，不含通道與子設備）；通道 = parent 底下的通道，另外列；畫面 = 攝影機 + 通道（影像牆用，camerasOf）；
 *   離線分「離線的設備」與「離線的通道」（parent 在線、通道離線只算通道）。
 * 總覽、設備清單、韌體頁都用這個，不各算各的。
 */
export interface DeviceCounts {
  devices: number;
  channels: number;
  onlineDevices: number;
  offlineDevices: number;
  offlineChannels: number;
}

export function countDevices(tree: Pick<VmsDevice, 'presence' | 'children'>[]): DeviceCounts {
  const out: DeviceCounts = { devices: 0, channels: 0, onlineDevices: 0, offlineDevices: 0, offlineChannels: 0 };
  for (const d of tree) {
    out.devices++;
    if (d.presence === 'online') out.onlineDevices++;
    else if (d.presence === 'offline') out.offlineDevices++;
    for (const c of d.children) {
      out.channels++;
      if (d.presence === 'online' && c.presence === 'offline') out.offlineChannels++;
    }
  }
  return out;
}

/**
 * 機種欄的顯示（SPEC #notify-177 設備名稱單一來源「不落到機種代碼」；第二輪 R2-P2-2）：機種全名要平台設備目錄補 display name（後端待辦），
 * 補上之前先把 Kinio 家族換成產品名、其他型號寫成產品型號的樣子（大寫，「ls-2504ac-k1」→「LS-2504AC-K1」），不露小寫代碼。
 */
/** AID 的序號末 4 碼（「GEN-7SMF53CH92W92FMM-Q」→「2FMM」），沒取名的設備用來區分。 */
export function aidTail(ref: string): string {
  const mid = ref.split('-').length >= 3 ? ref.split('-').slice(1, -1).join('') : ref.replace(/[^A-Za-z0-9]/g, '');
  return mid.slice(-4).toUpperCase();
}

/** things[].mac_suffix（Kinio Node / Wi-Fi 基地台的 MAC 後 6 碼；其他機種沒有）。 */
function macSuffixOf(o: Record<string, unknown>): { macSuffix?: string } {
  return typeof o.mac_suffix === 'string' && o.mac_suffix ? { macSuffix: o.mac_suffix } : {};
}

/** 平台帶的 model_name / model_source（空字串、非字串當沒有）。 */
export function modelNameOf(o: Record<string, unknown> | null | undefined): { modelName?: string; modelSource?: string } {
  const out: { modelName?: string; modelSource?: string } = {};
  if (typeof o?.model_name === 'string' && o.model_name.trim()) out.modelName = o.model_name.trim();
  if (typeof o?.model_source === 'string' && o.model_source) out.modelSource = o.model_source;
  return out;
}

/**
 * 機種顯示（全站一個函式；第五輪 7.4 後端 model_name）：平台的 model_name → generic 寫「RTSP 攝影機（沒有型號資訊）」→
 * 型號代號轉大寫（modelLabel）→ 什麼都沒有寫「還沒取得型號」，不寫「—」。
 */
export function modelDisplay(t: (k: string) => string, d: Pick<VmsDevice, 'model' | 'modelName' | 'modelSource'>): string {
  // 第三方攝影機回報的原字串（model_source = reported，例「ONVIF_ICAMERA MC800S5W_AF」）前面標「ONVIF 攝影機 ·」，看得出是對方報的（第六輪 B6-13、R6-P2-3）
  if (d.modelName && d.modelSource === 'reported') return `${t('devices.model_onvif')} · ${d.modelName}`;
  if (d.modelName) return d.modelName;
  if (d.modelSource === 'generic') return t('devices.model_generic');
  return modelLabel(d.model) || t('devices.model_unknown');
}

export function modelLabel(model: string | undefined): string {
  const m = (model ?? '').trim();
  if (!m) return '';
  if (/^kinio-station(-|$)/i.test(m)) return 'Kinio Station';
  if (/^kinio-node(-|$)/i.test(m)) return 'Kinio Node';
  return m.toUpperCase();
}
